import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Bell, Check, CheckCheck, Trash2, Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = { info: Info, success: CheckCircle, warning: AlertTriangle, error: XCircle, workflow: Bell, system: Bell };
const TYPE_COLORS = { info: '#1E5A8A', success: '#1F7A5A', warning: '#9A6B1A', error: '#B3261E', workflow: '#B89B5E', system: '#5B616E' };

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list({ unread_only: filter === 'unread', limit: 50 });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  };

  const markAllRead = async () => {
    try { await notificationsAPI.markRead([]); toast.success('All marked as read'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const markRead = async (ids) => {
    try { await notificationsAPI.markRead(ids); loadData(); }
    catch {}
  };

  const deleteNotif = async (id) => {
    try { await notificationsAPI.delete(id); loadData(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-[#B89B5E]" /> Notifications
            {unreadCount > 0 && <Badge variant="destructive" className="ml-2">{unreadCount} unread</Badge>}
          </h1>
          <p className="text-sm text-[#5B616E] mt-1">Workflow updates, assignments, and system alerts</p>
        </div>
        <div className="flex gap-2">
          <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')} className={filter === 'all' ? 'bg-[#121316] text-white' : ''}>All</Button>
          <Button variant={filter === 'unread' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('unread')} className={filter === 'unread' ? 'bg-[#121316] text-white' : ''}>Unread</Button>
          {unreadCount > 0 && <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn"><CheckCheck className="w-4 h-4 mr-1" /> Mark All Read</Button>}
        </div>
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : notifications.length > 0 ? (
            <div className="space-y-2">
              {notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                const color = TYPE_COLORS[n.type] || '#5B616E';
                return (
                  <div key={n.id} className={`flex items-start gap-3 p-3 rounded-lg transition-colors group ${n.is_read ? 'opacity-60 hover:opacity-80' : 'bg-[#FBF7EF] hover:bg-[#F5F0E6]'}`} data-testid="notification-item">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs text-[#5B616E] mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-[#5B616E] mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                      {!n.is_read && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => markRead([n.id])}><Check className="w-3 h-3" /></Button>}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => deleteNotif(n.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-[#5B616E]">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No notifications</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
